import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface FeeType {
  id: string;
  name: string;
  active: boolean;
}

interface Currency {
  id: string;
  code: string;
  name: string;
  active: boolean;
}

interface UnitFeeConfig {
  feeTypeId: string;
  currencyId: string;
  daily: number;
  weekly: number;
  monthly: number;
  bimonthly: number;
  quarterly: number;
  semiAnnual: number;
  annual: number;
  oneTime: number;
}

interface UnitCreateDialogProps {
  open: boolean;
  propertyId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const UnitCreateDialog: React.FC<UnitCreateDialogProps> = ({
  open,
  propertyId,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    number: '',
    rooms: 1,
    bathrooms: 1,
    floor: 1,
    areaM2: 0,
    includeFees: false
  });

  const [feeConfigs, setFeeConfigs] = useState<UnitFeeConfig[]>([]);
  const [feeTypes, setFeeTypes] = useState<FeeType[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loadingFeeTypes, setLoadingFeeTypes] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();

  // Fetch fee types and currencies when dialog opens
  useEffect(() => {
    if (open) {
      fetchFeeTypes();
      fetchCurrencies();
    }
  }, [open]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        number: '',
        rooms: 1,
        bathrooms: 1,
        floor: 1,
        areaM2: 0,
        includeFees: false
      });
      setFeeConfigs([]);
      setErrors({});
    }
  }, [open]);

  const fetchFeeTypes = async () => {
    setLoadingFeeTypes(true);
    try {
      const response = await DataService.get('api/fee-types/all');
      console.log('Fee Types API Response:', response);

      if (response.ok) {
        const responseData = await response.json();
        console.log('Fee Types Response Data:', responseData);

        let feeTypeList: FeeType[] = [];

        // Handle different response formats
        if (responseData.success && responseData.data) {
          // Format: { success: true, data: [...] }
          feeTypeList = responseData.data.filter((ft: FeeType) => ft.active);
        } else if (Array.isArray(responseData)) {
          // Format: [...]
          feeTypeList = responseData.filter((ft: FeeType) => ft.active);
        } else if (responseData.success && Array.isArray(responseData.result)) {
          // Format: { success: true, result: [...] }
          feeTypeList = responseData.result.filter((ft: FeeType) => ft.active);
        } else if (Array.isArray(responseData.result)) {
          // Format: { result: [...] }
          feeTypeList = responseData.result.filter((ft: FeeType) => ft.active);
        }

        console.log('Filtered Fee Types:', feeTypeList);
        setFeeTypes(feeTypeList);
      } else {
        console.error('Failed to fetch fee types:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching fee types:', error);
      dispatch(showAlert({
        message: 'Failed to load fee types',
        type: 'error'
      }));
    } finally {
      setLoadingFeeTypes(false);
    }
  };

  const fetchCurrencies = async () => {
    setLoadingCurrencies(true);
    try {
      const response = await DataService.get('api/Currency/all');
      console.log('Currencies API Response:', response);

      if (response.ok) {
        const responseData = await response.json();
        console.log('Currencies Response Data:', responseData);

        let currencyList: Currency[] = [];

        // Handle different response formats
        if (responseData.success && responseData.data) {
          currencyList = responseData.data.filter((c: Currency) => c.active);
        } else if (Array.isArray(responseData)) {
          currencyList = responseData.filter((c: Currency) => c.active);
        } else if (responseData.success && Array.isArray(responseData.result)) {
          currencyList = responseData.result.filter((c: Currency) => c.active);
        } else if (Array.isArray(responseData.result)) {
          currencyList = responseData.result.filter((c: Currency) => c.active);
        }

        console.log('Filtered Currencies:', currencyList);
        setCurrencies(currencyList);
      } else {
        console.error('Failed to fetch currencies:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      dispatch(showAlert({
        message: 'Failed to load currencies',
        type: 'error'
      }));
    } finally {
      setLoadingCurrencies(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const addFeeConfig = () => {
    const newFeeConfig: UnitFeeConfig = {
      feeTypeId: '',
      currencyId: '',
      daily: 0,
      weekly: 0,
      monthly: 0,
      bimonthly: 0,
      quarterly: 0,
      semiAnnual: 0,
      annual: 0,
      oneTime: 0
    };
    setFeeConfigs([...feeConfigs, newFeeConfig]);
  };

  const removeFeeConfig = (index: number) => {
    setFeeConfigs(feeConfigs.filter((_, i) => i !== index));
  };

  const updateFeeConfig = (index: number, field: keyof UnitFeeConfig, value: any) => {
    const updated = [...feeConfigs];
    updated[index] = { ...updated[index], [field]: value };
    setFeeConfigs(updated);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.number.trim()) {
      newErrors.number = 'Unit number is required';
    }

    if (formData.rooms < 0) {
      newErrors.rooms = 'Rooms must be a positive number';
    }

    if (formData.bathrooms < 0) {
      newErrors.bathrooms = 'Bathrooms must be a positive number';
    }

    if (formData.floor < 0) {
      newErrors.floor = 'Floor must be a positive number';
    }

    if (formData.areaM2 <= 0) {
      newErrors.areaM2 = 'Area must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);

    try {
      // Create unit payload as an array (API expects an array)
      const payload = [{
        propertyId: propertyId,
        number: formData.number.trim(),
        rooms: formData.rooms,
        bathrooms: formData.bathrooms,
        floor: formData.floor,
        areaM2: formData.areaM2,
        includeFees: formData.includeFees,
        includeAppliances: false, // Always false, removed from UI
        unitFeeConfigs: formData.includeFees
          ? feeConfigs
              .filter(fc => fc.feeTypeId && fc.currencyId) // Only include configs with both feeTypeId and currencyId
              .map(fc => ({
                feeTypeId: fc.feeTypeId,
                currencyId: fc.currencyId,
                daily: fc.daily,
                weekly: fc.weekly,
                monthly: fc.monthly,
                bimonthly: fc.bimonthly,
                quarterly: fc.quarterly,
                semiAnnual: fc.semiAnnual,
                annual: fc.annual,
                oneTime: fc.oneTime
                // unitId is not included when creating, it will be assigned by the backend
              }))
          : [],
        appliances: [] // Always empty, removed from UI
      }];

      console.log('Creating unit with data:', payload);

      const response = await DataService.post('api/unit/create', payload);

      if (response.ok) {
        dispatch(showAlert({
          message: 'Unit created successfully!',
          type: 'success'
        }));
        onSuccess();
      } else {
        let errorMessage = 'Failed to create unit';
        try {
          const errorData = await response.json();
          console.error('API Error Response:', errorData);
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          errorMessage = `Failed to create unit: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating unit:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create unit. Please try again.';
      dispatch(showAlert({
        message: errorMessage,
        type: 'error'
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '50vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          Create New Unit
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add a new unit to this property
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Unit Information */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HomeIcon color="primary" />
                Unit Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Unit Number"
                    value={formData.number}
                    onChange={(e) => handleInputChange('number', e.target.value)}
                    error={!!errors.number}
                    helperText={errors.number}
                    disabled={submitting}
                    required
                    placeholder="e.g., u12"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Floor"
                    type="number"
                    value={formData.floor}
                    onChange={(e) => handleInputChange('floor', parseInt(e.target.value) || 0)}
                    error={!!errors.floor}
                    helperText={errors.floor}
                    disabled={submitting}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Rooms"
                    type="number"
                    value={formData.rooms}
                    onChange={(e) => handleInputChange('rooms', parseInt(e.target.value) || 0)}
                    error={!!errors.rooms}
                    helperText={errors.rooms}
                    disabled={submitting}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Bathrooms"
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) => handleInputChange('bathrooms', parseInt(e.target.value) || 0)}
                    error={!!errors.bathrooms}
                    helperText={errors.bathrooms}
                    disabled={submitting}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Area (m²)"
                    type="number"
                    value={formData.areaM2}
                    onChange={(e) => handleInputChange('areaM2', parseFloat(e.target.value) || 0)}
                    error={!!errors.areaM2}
                    helperText={errors.areaM2}
                    disabled={submitting}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.includeFees}
                        onChange={(e) => handleInputChange('includeFees', e.target.checked)}
                        disabled={submitting}
                      />
                    }
                    label="Include Fees"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Fee Configurations */}
          <Collapse in={formData.includeFees}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoneyIcon color="primary" />
                    Fee Configurations (Optional)
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addFeeConfig}
                    disabled={submitting}
                  >
                    Add Fee Config
                  </Button>
                </Box>

                {feeConfigs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No fee configurations added. Click "Add Fee Config" to add one.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {feeConfigs.map((feeConfig, index) => (
                      <Card key={index} variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Fee Config #{index + 1}
                            </Typography>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeFeeConfig(index)}
                              disabled={submitting}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Fee Type *</InputLabel>
                                <Select
                                  value={feeConfig.feeTypeId}
                                  label="Fee Type *"
                                  onChange={(e) => updateFeeConfig(index, 'feeTypeId', e.target.value)}
                                  disabled={submitting || loadingFeeTypes}
                                >
                                  <MenuItem value="">
                                    <em>Select a fee type</em>
                                  </MenuItem>
                                  {feeTypes.map((feeType) => (
                                    <MenuItem key={feeType.id} value={feeType.id}>
                                      {feeType.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Currency *</InputLabel>
                                <Select
                                  value={feeConfig.currencyId}
                                  label="Currency *"
                                  onChange={(e) => updateFeeConfig(index, 'currencyId', e.target.value)}
                                  disabled={submitting || loadingCurrencies}
                                >
                                  <MenuItem value="">
                                    <em>Select a currency</em>
                                  </MenuItem>
                                  {currencies.map((currency) => (
                                    <MenuItem key={currency.id} value={currency.id}>
                                      {currency.code} - {currency.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Daily"
                                type="number"
                                value={feeConfig.daily}
                                onChange={(e) => updateFeeConfig(index, 'daily', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Weekly"
                                type="number"
                                value={feeConfig.weekly}
                                onChange={(e) => updateFeeConfig(index, 'weekly', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Monthly"
                                type="number"
                                value={feeConfig.monthly}
                                onChange={(e) => updateFeeConfig(index, 'monthly', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Bimonthly"
                                type="number"
                                value={feeConfig.bimonthly}
                                onChange={(e) => updateFeeConfig(index, 'bimonthly', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Quarterly"
                                type="number"
                                value={feeConfig.quarterly}
                                onChange={(e) => updateFeeConfig(index, 'quarterly', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Semi-Annual"
                                type="number"
                                value={feeConfig.semiAnnual}
                                onChange={(e) => updateFeeConfig(index, 'semiAnnual', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Annual"
                                type="number"
                                value={feeConfig.annual}
                                onChange={(e) => updateFeeConfig(index, 'annual', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="One Time"
                                type="number"
                                value={feeConfig.oneTime}
                                onChange={(e) => updateFeeConfig(index, 'oneTime', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Collapse>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          variant="outlined"
          size="large"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          variant="contained"
          size="large"
          startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {submitting ? 'Creating...' : 'Create Unit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnitCreateDialog;
